import { MoreHorizontalIcon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { TableBody, TableCell, TableRow } from '@/shared/components/ui/table';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

export type TableRowAction = {
  action: React.ReactNode;
  onClick: () => void;
};

export type TableRowProps = {
  className?: string;
  props: TableRowItemProps[];
  isFirstRow?: boolean;
  isLastRow?: boolean;
  showMenu?: boolean;
  onClick?: () => void;
  actions?: TableRowAction[];
  type: 'single';
};

export type TableDoubleRowProps = {
  isLastRow?: boolean;
  firstRow: TableRowItemProps[];
  actions?: TableRowAction[];
  secondRow: TableRowItemProps[];
  showMenu?: boolean;
  onClick?: () => void;
  type: 'double';
};

export type TableRowItemProps = {
  value: TableRowItemValue;
  isLeftMost?: boolean;
  isRightMost?: boolean;
  width?: string;
} | null;

type TableRowItemValue =
  | TableRowItemStringValue
  | TableRowItemNumberValue
  | TableRowItemDateValue
  | TableRowItemNameWithAvatarValue
  | TableRowItemCustomValue;

type TableRowItemStringValue = {
  text: string;
  type: 'string';
};

type TableRowItemNumberValue = {
  number: number | null;
  type: 'number';
};

type TableRowItemDateValue = {
  date: Date | null;
  overdue?: boolean;
  type: 'date';
};

type TableRowItemNameWithAvatarValue = {
  name: string;
  avatar: string;
  type: 'nameWithAvatar';
};

type TableRowItemCustomValue = {
  item: React.ReactNode;
  type: 'custom';
};

export const CustomTableRow = ({
  className,
  props,
  isFirstRow,
  isLastRow,
  showMenu = true,
  onClick,
  actions,
}: TableRowProps) => {
  return (
    <TableRow
      className={cn(
        'group relative flex flex-1 flex-row items-stretch gap-0 border-0 hover:bg-inherit',
        !isLastRow &&
          'after:absolute after:bottom-0 after:left-1 after:right-1 after:border-b after:border-white/10',
        onClick && 'cursor-pointer',
        isLastRow && 'mb-2',
        className,
      )}
      onClick={onClick}
    >
      {props.map((item, index) => {
        const isLastCell = index == props.length - 1;

        return TableRowCell({
          key: index.toString(),
          width: item?.width,
          child: (() => {
            const hasActions = actions && actions.length > 0;
            return (
              <div className="line-clamp-1 flex flex-1 flex-row items-center">
                {item && <ValueComponent value={item.value} />}

                {isLastCell && showMenu && hasActions && (
                  <div className="absolute right-3 z-10 flex flex-row">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="cursor-pointer" asChild>
                        <MoreHorizontalIcon className="size-6 text-white" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        className="z-[100] cursor-pointer"
                        side="left"
                        align="start"
                        sideOffset={8}
                        avoidCollisions={true}
                        collisionPadding={16}
                      >
                        {actions &&
                          actions.map((action, index) => (
                            <DropdownMenuItem
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick();
                              }}
                            >
                              {action.action}
                            </DropdownMenuItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            );
          })(),
          isFirstCell: index == 0,
          isLastCell: isLastCell,
          isFirstRow,
          isLastRow,
        });
      })}
    </TableRow>
  );
};

export const CustomDoubleRow = ({
  firstRow,
  secondRow,
  showMenu,
  actions,
  onClick,
}: TableDoubleRowProps) => {
  return (
    <TableBody className="group">
      <CustomTableRow
        props={firstRow}
        isFirstRow={true}
        actions={actions}
        showMenu={showMenu}
        type="single"
        onClick={onClick}
      />
      <CustomTableRow
        props={secondRow}
        isLastRow={true}
        showMenu={false}
        type="single"
        onClick={onClick}
      />
    </TableBody>
  );
};

const ValueComponent = ({ value }: { value: TableRowItemValue }) => {
  const { t } = useTranslation('shared');

  switch (value.type) {
    case 'string':
      return (
        <span className="overflow-hidden truncate text-ellipsis whitespace-pre-wrap">
          {value.text}
        </span>
      );
    case 'number':
      return <NumberComponent number={value.number} />;
    case 'date':
      return (
        <div className={value.overdue ? 'text-error-red' : ''}>
          {value.date
            ? formatDate({ date: value.date, formatType: 'digits' })
            : '-'}
          {value.overdue && ' ( ' + t('overdue').toLowerCase() + ' )'}
        </div>
      );
    case 'nameWithAvatar':
      return (
        <NameWithAvatarComponent name={value.name} avatar={value.avatar} />
      );
    case 'custom':
      return value.item;
  }
};

const TableRowCell = ({
  child,
  key,
  isFirstCell,
  isLastCell,
  isFirstRow,
  isLastRow,
  width,
}: {
  child: React.ReactNode;
  key?: string;
  isFirstCell?: boolean;
  isLastCell?: boolean;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  width?: string;
}) => {
  return (
    <TableCell
      key={key}
      className={cn(
        'flex shrink-0 items-center bg-clip-border px-4 py-3 text-start text-2xs text-white group-hover:bg-white/5',
        width || 'flex-1', // Use custom width if provided, else use flex-1
        isFirstRow && 'rounded-t-xs border-t border-t-white/50',
        isLastRow && 'rounded-b-xs border-b border-b-white/50',
        isFirstCell && 'border-l border-l-white/50',
        isLastCell && 'border-r border-r-white/50',
      )}
    >
      {child}
    </TableCell>
  );
};

const NumberComponent = ({ number }: { number: number | null }) => {
  return (
    <div className="rounded-full border-none bg-[#FEFEFE] px-2 py-[3px] text-2xs font-normal text-black">
      {number ?? '-'}
    </div>
  );
};

const NameWithAvatarComponent = ({
  name,
  avatar,
}: {
  name: string;
  avatar: string;
}) => {
  return (
    <div className="flex items-center gap-2">
      <div className="h-6 w-6 rounded-full bg-black px-1 text-white">
        <div className="h-full w-full flex-row content-center items-center justify-center overflow-clip text-center">
          <p className="flex-1 text-ellipsis text-3xs font-bold">{avatar}</p>
        </div>
      </div>
      {name}
    </div>
  );
};
